import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { isGoalkeeper } from '../../utils/playerDiscovery';
import PlayerRoleBadge from '../playerStatistics/PlayerRoleBadge';

const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const TYPES = ['yellow', 'doubleYellow', 'red'];
const countKeys = ['totalAmarillas', 'totalSegundas', 'totalRojas'];

export default function ReportDiscipline({ cards, matches }) {
  const { t, i18n } = useTranslation('reports');
  const theme = useTheme();
  const format = value => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(number(value));
  const counts = countKeys.map(key => number(cards?.[key]));
  const total = counts.reduce((sum, count) => sum + count, 0);
  const rate = (count, played) => number(played) > 0 ? format(number(count) / number(played)) : '0';
  return <div className="rr-discipline">
    <section className="rr-panel" aria-label={t('sections.cards')}>
      <header><span className="rr-eyebrow">{t('modern.discipline.eyebrow')}</span>
        <h2>{t('sections.cards')}</h2><p>{t('modern.discipline.note')}</p></header>
      {(!cards || cards.sourceAvailable === false) && <p className="rr-empty">{t('modern.disciplineEmpty')}</p>}
      <dl className="rr-card-totals">{TYPES.map((type, index) => <div key={type} className={`rr-card-total rr-card-total--${type}`}>
        <dt><span className="rr-card-symbol" aria-hidden="true" />{t(`modern.discipline.${type}`)}</dt>
        <dd>{format(counts[index])}
          <span className="rr-card-share" aria-hidden="true"><i style={{ width: `${total > 0 ? counts[index] / total * 100 : 0}%` }} /></span>
        </dd>
      </div>)}</dl>
      <dl className="rr-discipline-rates" aria-label={t('cards.averages')}>
        {[
          ['totalPerGame', total, matches?.partidosJugados],
          ['homePerGame', cards?.local, matches?.partidosJugadosLocal],
          ['awayPerGame', cards?.visitante, matches?.partidosJugadosVisitante],
        ].map(([label, count, played]) => <div key={label}><dt>{t(`cards.${label}`)}</dt>
          <dd>{rate(count, played)}</dd></div>)}
      </dl>
      <div className="rr-table-scroll" role="region" aria-label={t('cards.breakdown')} tabIndex={0}>
        <table className="rr-people-table" aria-label={t('cards.breakdown')}>
          <thead><tr><th scope="col">{t('modern.discipline.venue')}</th>
            {TYPES.map(type => <th key={type} scope="col">{t(`modern.discipline.${type}`)}</th>)}
            <th scope="col">{t('stats.total')}</th></tr></thead>
          <tbody>{['Local', 'Visitante'].map((venue, index) => {
            const fields = ['amarillas', 'segundas', 'rojas'].map(prefix => `${prefix}${venue}`);
            return <tr key={venue}><th scope="row">{t(index === 0 ? 'stats.local' : 'stats.visitante')}</th>
              {fields.map(key => <td key={key}>{format(cards?.[key])}</td>)}
              <td>{format(cards?.[index === 0 ? 'local' : 'visitante'])}</td></tr>;
          })}<tr className="rr-table-total"><th scope="row">{t('stats.total')}</th>
            {counts.map((count, index) => <td key={TYPES[index]}>{format(count)}</td>)}<td>{format(total)}</td></tr></tbody>
        </table>
      </div>
    </section>
    <div className="rr-discipline-detail">
      <section className="rr-panel" aria-label={t('sections.mostCarded')}>
        <header><span className="rr-eyebrow">{t('modern.discipline.playersEyebrow')}</span>
          <h2>{t('sections.mostCarded')}</h2></header>
        {cards?.jugadores?.length ? <div className="rr-table-scroll" role="region" aria-label={t('sections.mostCarded')} tabIndex={0}>
          <table className="rr-people-table" aria-label={t('sections.mostCarded')}>
            <thead><tr><th scope="col">{t('table.number')}</th><th scope="col">{t('table.name')}</th>
              {TYPES.map(type => <th scope="col" key={type}>{t(`modern.discipline.${type}`)}</th>)}
              <th scope="col">{t('stats.total')}</th></tr></thead>
            <tbody>{cards.jugadores.map((player, index) => <tr key={player.id ?? index}
              className={isGoalkeeper(player) ? 'player-goalkeeper' : undefined} data-theme={theme.palette.mode}>
              <td><span className="rr-shirt">{player.dorsalMasUsado ?? 0}</span></td>
              <th scope="row">{player.nombre}<PlayerRoleBadge player={player} /></th>
              {['amarillas', 'segundas', 'rojas', 'total'].map(key => <td key={key}>{format(player[key])}</td>)}
            </tr>)}</tbody>
          </table>
        </div> : <p className="rr-empty">{t('empty.noCardsDesc')}</p>}
      </section>
      <section className="rr-panel" aria-label={t('sections.cardAnalysis')}>
        <header><span className="rr-eyebrow">{t('modern.discipline.contextEyebrow')}</span>
          <h2>{t('sections.cardAnalysis')}</h2></header>
        <h3 className="rr-detail-title">{t('cards.byMoment')}</h3>
        <dl className="rr-discipline-states">{['ganando', 'empatando', 'perdiendo'].map((key, index) =>
          <div key={key}><dt><i className={`rr-state-dot rr-state-dot--${index}`} aria-hidden="true" />
            {t(`stats.${['winning', 'drawing', 'losing'][index]}`)}</dt><dd>{format(cards?.[key])}</dd></div>)}</dl>
        <h3 className="rr-detail-title">{t('cards.homeVsAway')}</h3>
        <dl className="rr-discipline-venues">{['local', 'visitante'].map(key => <div key={key}>
          <dt>{t(`stats.${key}`)}</dt><dd>{format(cards?.[key])}</dd></div>)}</dl>
      </section>
    </div>
  </div>;
}
